import { useState } from "react";

import { ExportableArray } from "@impero-com/common/utils/export";
import {
    formatFileNameForExport,
    formatDateTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import { TextToolState } from "@impero-com/ui";
import { Column, Row } from "@tanstack/react-table";
import saveAs from "save-as";

import { isActivityOverdue } from "org/controlTesting/overdueUtils";
import { fetchTagsQuery, TagsQuerySelect } from "org/tags/queries/useTagsQuery";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { newExportMetadata } from "utils/export";

import {
    IanaTimezoneName,
    TActExportRow,
    TActTableRow,
} from "../../../../api/generated/model";

import { fetchTestingActivitiesExportQuery } from "./queries/fetchTestingActivitiesExportQuery";
import { TestingActivityColumnId } from "./types";

const columnHeaderMap: Record<TestingActivityColumnId, string> = {
    title: "Name",
    entitiesToTest: "Entities to test",
    tags: "Tags",
    dueDate: "Due date",
    userResponsible: "Responsible",
    effectiveness: "Effectiveness",
    status: "Status",
    samplingPeriodStart: "Sampling period start",
    samplingPeriodEnd: "Sampling period end",
    responsibleDueDate: "Responsible due date",
    firstReviewer: "First reviewer",
    firstReviewerDueDate: "First reviewer due date",
    secondReviewer: "Second reviewer",
    secondReviewerDueDate: "Second reviewer due date",
    submittedDate: "Submitted Date",
    reviewedDate: "Reviewed Date",
    secondReviewedDate: "Second reviewed date",
    description: "Description",
    selection: "Selection",
};
const TAGS_CATEGORY_COLUMN_PREFIX = "Metadata (Tags)";
const ENTITIES_TO_TEST_COLUMN_PREFIX = "Metadata (Entities to test)";

interface ExportColumn {
    header: string;
    rows: (string | undefined)[];
}
type ExportColumns = ExportColumn[];

interface TagsByCategory {
    [categoryName: string]: string[];
}

const RESPONSIBLE_CONCLUSION_TASK_PREFIX = "ResponsibleConclusionTask";

function getTagsByCategory(
    rowTagIds: string[],
    tagsData: TagsQuerySelect
): TagsByCategory {
    const { tags, tagCategories } = tagsData;
    const tagsByCategory: TagsByCategory = {};

    rowTagIds.forEach((tagId) => {
        const tag = tags.find((t) => t.id === tagId);
        if (!tag) {
            throw new Error(
                `Tag with id ${tagId} not found - query should be run before running this function`
            );
        }

        const tagCategory = tagCategories.find(
            (category) => category.id === tag.tagCategoryId
        );
        if (!tagCategory) {
            throw new Error("Expected to find tag category");
        }

        const categoryName = tagCategory.name;
        if (!tagsByCategory[categoryName]) {
            tagsByCategory[categoryName] = [];
        }
        tagsByCategory[categoryName].push(tag.name);
    });

    return tagsByCategory;
}

function getAllCategoryNames(
    filteredExportDataRows: TActExportRow[],
    tagsData: TagsQuerySelect,
    columnType: "tags" | "entitiesToTest"
): Set<string> {
    const allCategories = new Set<string>();

    filteredExportDataRows.forEach((row) => {
        const tagIds = columnType === "tags" ? row.tags : row.samplingTags;
        const tagsByCategory = getTagsByCategory(tagIds, tagsData);
        Object.keys(tagsByCategory).forEach((categoryName) => {
            allCategories.add(categoryName);
        });
    });

    return allCategories;
}
export function exportableTagsData(
    filteredExportDataRows: TActExportRow[],
    tagsData: TagsQuerySelect,
    columnType: "tags" | "entitiesToTest"
): ExportColumns {
    const columns: ExportColumns = [];

    // Get all unique tag categories
    const categories = getAllCategoryNames(
        filteredExportDataRows,
        tagsData,
        columnType
    );

    const sortedCategories = Array.from(categories).sort();
    sortedCategories.forEach((categoryName) => {
        const header = (() => {
            switch (columnType) {
                case "tags":
                    return `${TAGS_CATEGORY_COLUMN_PREFIX}: ${categoryName}`;
                case "entitiesToTest":
                    return `${ENTITIES_TO_TEST_COLUMN_PREFIX}: ${categoryName}`;
                default:
                    return assertNever(columnType);
            }
        })();

        const rows = filteredExportDataRows.map((row) => {
            const tagIds = columnType === "tags" ? row.tags : row.samplingTags;
            const tagsByCategory = getTagsByCategory(tagIds, tagsData);
            return tagsByCategory[categoryName]?.join(", ") || "";
        });

        columns.push({ header, rows });
    });

    return columns;
}

export async function exportableDataFromVisibleColumns(
    filteredExportDataRows: TActExportRow[],
    visibleColumnIds: TestingActivityColumnId[],
    timezone: IanaTimezoneName
): Promise<ExportColumns> {
    const tagsData =
        visibleColumnIds.includes("entitiesToTest") ||
        visibleColumnIds.includes("tags")
            ? await fetchTagsQuery()
            : null;

    const columns: ExportColumns = [];

    visibleColumnIds.forEach((columnId) => {
        const header = columnHeaderMap[columnId];

        if (!header) {
            return;
        }

        // Skip the original tags and entitiesToTest columns - we'll add flattened versions
        if (columnId === "tags" || columnId === "entitiesToTest") {
            return;
        }

        const rows = filteredExportDataRows.map((row) => {
            switch (columnId) {
                case "title":
                    return row.title;

                case "dueDate":
                    return formatDateTimeAndTimezone(row.dueDate, {
                        name: timezone,
                    });
                case "userResponsible":
                    return row.responsible.toString();
                case "effectiveness":
                    return row.effectiveness || "";
                case "status":
                    return getTestingActivityExportStatusLabel(row);
                case "samplingPeriodStart":
                    return `${row.samplingPeriod.periodStartMonth} ${row.samplingPeriod.periodStartYear}`;
                case "samplingPeriodEnd":
                    return `${row.samplingPeriod.periodEndMonth} ${row.samplingPeriod.periodEndYear}`;
                case "responsibleDueDate":
                    return formatDateTimeAndTimezone(
                        row.responsibleInfo.dueDate,
                        { name: timezone }
                    );
                case "firstReviewer":
                    return row.firstReviewInfo?.user?.toString() || "";
                case "firstReviewerDueDate":
                    return row.firstReviewInfo?.dueDate
                        ? formatDateTimeAndTimezone(
                              row.firstReviewInfo.dueDate,
                              { name: timezone }
                          )
                        : "";
                case "secondReviewer":
                    return row.secondReviewInfo?.user?.toString() || "";
                case "secondReviewerDueDate":
                    return row.secondReviewInfo?.dueDate
                        ? formatDateTimeAndTimezone(
                              row.secondReviewInfo.dueDate,
                              { name: timezone }
                          )
                        : "";
                case "submittedDate":
                    return row.responsibleInfo.completionDate
                        ? formatDateTimeAndTimezone(
                              row.responsibleInfo.completionDate,
                              { name: timezone }
                          )
                        : "";
                case "reviewedDate":
                    return row.firstReviewInfo?.completionDate
                        ? formatDateTimeAndTimezone(
                              row.firstReviewInfo.completionDate,
                              { name: timezone }
                          )
                        : "";
                case "secondReviewedDate":
                    return row.secondReviewInfo?.completionDate
                        ? formatDateTimeAndTimezone(
                              row.secondReviewInfo.completionDate,
                              { name: timezone }
                          )
                        : "";
                case "description":
                    return row.description;
                case "selection":
                    return "";
                default:
                    return assertNever(columnId);
            }
        });

        columns.push({ header, rows });
    });

    // Add flattened tag columns
    if (tagsData && visibleColumnIds.includes("tags")) {
        const tagColumns = exportableTagsData(
            filteredExportDataRows,
            tagsData,
            "tags"
        );
        columns.push(...tagColumns);
    }

    // Add flattened entities to test columns
    if (tagsData && visibleColumnIds.includes("entitiesToTest")) {
        const entitiesToTestColumns = exportableTagsData(
            filteredExportDataRows,
            tagsData,
            "entitiesToTest"
        );
        columns.push(...entitiesToTestColumns);
    }

    return columns;
}

function getTestingActivityExportStatusLabel(row: TActExportRow) {
    const isOverdue = isActivityOverdue(row);
    switch (row.status) {
        case "responsible": {
            return isOverdue
                ? "Overdue, waiting on responsible"
                : "Waiting on responsible";
        }
        case "firstReviewer": {
            return isOverdue
                ? "Overdue, waiting on reviewer"
                : "Waiting on reviewer";
        }
        case "secondReviewer": {
            return isOverdue
                ? "Overdue, waiting on second reviewer"
                : "Waiting on second reviewer";
        }
        case "completed":
            return "Completed";
        default:
            assertNever(row.status);
    }
}

export function exportableAnswersData(
    filteredExportDataRows: TActExportRow[]
): ExportColumns {
    if (filteredExportDataRows.length === 0) {
        return [];
    }

    const tasks = filteredExportDataRows[0]?.responsibleResult.tasks || [];

    return tasks.map((task, taskIndex) => ({
        header: `${RESPONSIBLE_CONCLUSION_TASK_PREFIX}_${taskIndex}_${task.taskType}_${task.title}`,
        rows: filteredExportDataRows.map(
            (row) => row.responsibleResult.tasks[taskIndex]?.answer || undefined
        ),
    }));
}

export async function activityRowsToExportableData(
    selectedRows: TActTableRow[],
    testingProgramId: number,
    visibleColumnIds: TestingActivityColumnId[],
    type: "WITH_ANSWERS" | "NORMAL",
    timezone: IanaTimezoneName
): Promise<ExportableArray> {
    const exportData =
        await fetchTestingActivitiesExportQuery(testingProgramId);

    const filteredExportDataRows = exportData.data.content.resource.filter(
        (row) =>
            selectedRows
                .map((selectedRow) => selectedRow.activityId)
                .includes(row.activityId)
    );

    const exportableData: ExportColumns =
        await exportableDataFromVisibleColumns(
            filteredExportDataRows,
            visibleColumnIds,
            timezone
        );

    const combinedColumns: ExportColumns = (() => {
        switch (type) {
            case "WITH_ANSWERS": {
                const exportableAnswers = exportableAnswersData(
                    filteredExportDataRows
                );
                return [...exportableData, ...exportableAnswers];
            }
            case "NORMAL":
                return exportableData;
            default:
                return assertNever(type);
        }
    })();

    const header = combinedColumns.map((column) => column.header);

    const content: string[][] =
        combinedColumns[0]?.rows.map((_, rowIndex) =>
            combinedColumns.map((column) => column.rows[rowIndex] || "")
        ) || [];

    return { header, content };
}

export type TestingActivityExportModalSubmitType = "WITH_ANSWERS" | "NORMAL";

interface UseTestingActivityExportUtilsProps {
    selectedRows: Row<TActTableRow>[];
    testingProgramId: number;
    visibleColumns: Column<TActTableRow>[];
}

export function useTestingActivityExportUtils({
    selectedRows,
    testingProgramId,
    visibleColumns,
}: UseTestingActivityExportUtilsProps) {
    const { t } = useTranslation();
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const timezone = useCurrentUserTimezone();

    const exportToolState: TextToolState =
        selectedRows.length > 0
            ? { type: "ENABLED" }
            : {
                  type: "DISABLED",
                  reason: t("TestingActivities_ExportReassignReason"),
              };

    const exportToExcel = async (
        exportableRows:
            | { header: string[]; content: any[][] }
            | Promise<{ header: string[]; content: any[][] }>,
        worksheetName: string,
        fileName: string
    ) => {
        const resolvedRows = await Promise.resolve(exportableRows);
        const metadata = newExportMetadata();
        const worksheet = XLSXUtils.tableToWorksheet(
            resolvedRows,
            undefined,
            metadata
        );
        const workbook = XLSXUtils.workbookWithSingleWorksheet(
            worksheetName,
            worksheet
        );
        const data = XLSXUtils.workbookToBinary(workbook);

        const blob = new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, formatFileNameForExport(fileName, "xlsx"));
    };

    const handleExportNoAnswers = async () => {
        const selectedRowsOriginal = selectedRows.map((row) => row.original);
        const exportableRows = await activityRowsToExportableData(
            selectedRowsOriginal,
            testingProgramId,
            visibleColumnIds,
            "NORMAL",
            timezone
        );
        if (!exportableRows.header) {
            throw new Error("No header data available for export");
        }
        await exportToExcel(
            {
                header: exportableRows.header,
                content: exportableRows.content,
            },
            "TestingActivities",
            "TestingActivities"
        );
    };
    const visibleColumnIds = visibleColumns.reduce<TestingActivityColumnId[]>(
        (acc, column) => {
            if (column.getIsVisible()) {
                return [...acc, column.id as TestingActivityColumnId];
            }
            return acc;
        },
        []
    );
    const handleExportWithAnswers = async () => {
        const selectedRowsOriginal = selectedRows.map((row) => row.original);
        const exportableRows = await activityRowsToExportableData(
            selectedRowsOriginal,
            testingProgramId,
            visibleColumnIds,
            "WITH_ANSWERS",
            timezone
        );
        if (!exportableRows.header) {
            throw new Error("No header data available for export");
        }
        await exportToExcel(
            {
                header: exportableRows.header,
                content: exportableRows.content,
            },
            "TestingActivitiesWithAnswers",
            "TestingActivitiesWithAnswers"
        );
    };

    const handleExport = async (type: TestingActivityExportModalSubmitType) => {
        setIsExporting(true);
        try {
            switch (type) {
                case "NORMAL": {
                    await handleExportNoAnswers();
                    setExportModalOpen(false);
                    return;
                }
                case "WITH_ANSWERS": {
                    await handleExportWithAnswers();
                    setExportModalOpen(false);
                    return;
                }
                default: {
                    throw new Error(`Unexpected export type: ${type}`);
                }
            }
        } finally {
            setIsExporting(false);
        }
    };

    const openExportModal = () => setExportModalOpen(true);
    const closeExportModal = () => setExportModalOpen(false);

    return {
        exportToolState,
        exportModalOpen,
        openExportModal,
        closeExportModal,
        handleExport,
        isExporting,
    };
}
